import type { PageEntry, Node, Entry } from 'contensis-delivery-api';
import type { PagedList } from 'contensis-core-api';
import slugify from 'slugify';

import cmsParser from 'Common/util/cmsParser';
import { getSlugFromContentType } from 'Common/util/ben';

import type { Props } from './Listing';

type FilterTypes = 'subjectArea' | 'storyType';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    subjectsTaxonomy: Entry;
    storyClassificationTaxonomy: Entry;
    customTagsTaxonomy: Entry | null;
    listingContentTypes: string[];
    activeFilterQueries: Record<FilterTypes, string[] | null>;
    activeSearchTerm: string;
    listing: PagedList<Entry>;
};

const datamap = (data: PageData): Props => {
    const {
        entry,
        ancestors,
        subjectsTaxonomy,
        storyClassificationTaxonomy,
        customTagsTaxonomy,
        listingContentTypes,
        activeFilterQueries,
        activeSearchTerm,
        listing
    } = data;

    const { title, sys, heading, sectionNavigation, listingCard } = entry;

    // Add common filters between pages
    const filters = [
        {
            type: 'subjectArea',
            label: 'Subject area',
            options: subjectsTaxonomy.children.map(subject => ({
                id: subject.key,
                name: slugify(subject.name, { lower: true }),
                label: subject.name,
                value: subject.key
            }))
        },
        {
            type: 'storyType',
            label: 'Story type',
            options: storyClassificationTaxonomy.children.map(subject => ({
                id: subject.key,
                name: slugify(subject.name, { lower: true }),
                label: subject.name,
                value: subject.key
            }))
        }
    ];

    // Add custom tags filter for independent blogs
    if (customTagsTaxonomy) {
        filters.push({
            type: 'blogType',
            label: 'Blog type',
            options: customTagsTaxonomy.children.map(subject => ({
                id: subject.key,
                name: slugify(subject.name, { lower: true }),
                label: subject.name,
                value: subject.key
            }))
        });
    }

    // Add media type filter for the main search page
    if (listingContentTypes.length > 1) {
        filters.push({
            type: 'mediaType',
            label: 'Media type',
            options: listingContentTypes.map(contentType => {
                const convertedContentName = getSlugFromContentType(contentType);
                const contentName =
                    convertedContentName.charAt(0).toUpperCase() + convertedContentName.slice(1);
                return {
                    id: contentType,
                    name: contentName,
                    label: contentName,
                    value: contentType
                };
            })
        });
    }

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            heading: heading,
            sectionNav: {
                links: sectionNavigation.link.map(item => cmsParser.link(item))
            }
        },
        filters: {
            id: 'media-hub',
            filters: filters,
            initialActiveFilters: Object.keys(activeFilterQueries)
                .map((key: FilterTypes) => {
                    const activeFilters = activeFilterQueries[key];

                    if (activeFilters) {
                        return activeFilters.map(activeFilter => ({
                            id: activeFilter,
                            type: key,
                            label: filters
                                .find(filter => filter.type === key)
                                ?.options.find(filter => filter.value === activeFilter).label
                        }));
                    }
                    return [];
                })
                .flat(),
            initialSearchTerm: activeSearchTerm,
            numberOfResults: listing.totalCount
        },
        listing: {
            results: listing.items.map(item => cmsParser.benCard(item, false)),
            pagination: {
                id: 'listing-pagination',
                initialPageIndex: listing.pageIndex,
                pageSize: listing.pageSize,
                totalCount: listing.totalCount
            },
            promo: {
                image: cmsParser.image(listingCard.image),
                heading: listingCard.heading,
                description: listingCard.description,
                button: {
                    ...cmsParser.linkButton(listingCard.link),
                    variant: 'accentFourTertiary'
                }
            }
        }
    };
};

export default datamap;
